import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Modal, Form, Badge, Alert, Table, Spinner, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faClock,
    faPlus,
    faEdit,
    faTrash,
    faPlay,
    faPause,
    faStop,
    faRotateRight,
    faCalendarDays,
    faRobot,
    faBolt,
    faChartLine,
    faExclamationTriangle,
    faCheck,
    faEllipsisVertical,
    faHistory,
    faSyncAlt,
    faInfoCircle,
    faCog,
    faTimeline,
    faChartPie
} from '@fortawesome/free-solid-svg-icons';
import { useSellerbotRoutines, SellerbotService, RoutineTask } from '../../service/sellerbotService';

// ==================== 🎯 INTERFACES ====================

interface RoutinesTabProps {
    language: 'es' | 'en';
    onRoutinesChange?: (routines: RoutineTask[]) => void;
}

interface RoutineFormData {
    name: string;
    taskType: 'follow_up' | 'lead_generation' | 'data_sync' | 'report_generation' | 'auto_detect' | 'campaign_trigger';
    description: string;
    schedule: string;
    isActive: boolean;
    config: {
        priority?: 'low' | 'medium' | 'high';
        maxExecutions?: number;
        timeout?: number;
        retryAttempts?: number;
        [key: string]: any;
    };
}

// ==================== 🎨 ESTILOS CSS ====================
const customStyles = `
  .routines-tab .routine-card {
    transition: all 0.3s ease !important;
    border: 1px solid #e9ecef !important;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  }

  .routines-tab .routine-card:hover {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
    transform: translateY(-2px) !important;
  }

  .routines-tab .status-running {
    background: linear-gradient(135deg, #d4edda, #c3e6cb);
    border-left: 4px solid #28a745;
  }

  .routines-tab .status-idle {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-left: 4px solid #6c757d;
  }

  .routines-tab .status-error {
    background: linear-gradient(135deg, #f8d7da, #f5c6cb);
    border-left: 4px solid #dc3545;
  }

  .routines-tab .kpi-card {
    transition: all 0.3s ease;
    border-left: 4px solid transparent;
  }
  
  .routines-tab .kpi-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
    border-left-color: var(--bs-primary);
  }

  .routines-tab .schedule-badge {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-family: 'Courier New', monospace;
    font-size: 0.8rem;
  }

  @media (max-width: 768px) {
    .routines-tab .routine-card:hover {
      transform: none;
    }
  }
`;

// ==================== 🎯 COMPONENTE PRINCIPAL ====================
const RoutinesTab: React.FC<RoutinesTabProps> = ({ language, onRoutinesChange }) => {
    
    // ==================== HOOKS DE DATOS ====================
    const { routines, loading, error, refetch, createRoutine, deleteRoutine } = useSellerbotRoutines();

    // ==================== ESTADOS PRINCIPALES ====================
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingRoutine, setEditingRoutine] = useState<RoutineTask | null>(null);
    const [routineFormData, setRoutineFormData] = useState<RoutineFormData>({
        name: '',
        taskType: 'follow_up',
        description: '',
        schedule: '0 */2 * * *', // Cada 2 horas por defecto
        isActive: true,
        config: {
            priority: 'medium',
            maxExecutions: 10,
            timeout: 300,
            retryAttempts: 3
        }
    });
    const [selectedRoutine, setSelectedRoutine] = useState<RoutineTask | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const [localSuccess, setLocalSuccess] = useState<string | null>(null);

    // ==================== EFECTOS ====================
    
    useEffect(() => {
        if (onRoutinesChange) {
            onRoutinesChange(routines);
        }
    }, [routines, onRoutinesChange]);

    // ==================== FUNCIONES PRINCIPALES ====================

    const handleCreateRoutine = async () => {
        try {
            setLocalError(null);
            
            const routineData = {
                name: routineFormData.name,
                task_type: routineFormData.taskType,
                description: routineFormData.description,
                schedule: routineFormData.schedule,
                is_active: routineFormData.isActive,
                config: routineFormData.config
            };

            await createRoutine(routineData);
            setLocalSuccess(getText('routineCreated'));
            setShowCreateModal(false);
            resetForm();
            setTimeout(() => setLocalSuccess(null), 3000);
        } catch (error: any) {
            setLocalError(error.message || getText('errorCreatingRoutine'));
            setTimeout(() => setLocalError(null), 5000);
        }
    };

    const handleDeleteRoutine = async () => {
        if (!selectedRoutine) return;
        
        try {
            setLocalError(null);
            await deleteRoutine(selectedRoutine.id);
            setLocalSuccess(getText('routineDeleted'));
            setShowDeleteModal(false);
            setSelectedRoutine(null);
            setTimeout(() => setLocalSuccess(null), 3000);
        } catch (error: any) {
            setLocalError(error.message || getText('errorDeletingRoutine'));
            setTimeout(() => setLocalError(null), 5000);
        }
    };

    const handleToggleRoutine = async (routineId: string) => {
        try {
            await SellerbotService.toggleRoutine(routineId);
            await refetch();
            setLocalSuccess(getText('routineToggled'));
            setTimeout(() => setLocalSuccess(null), 2000);
        } catch (error: any) {
            setLocalError(error.message || getText('errorTogglingRoutine'));
            setTimeout(() => setLocalError(null), 5000);
        }
    };

    const handleExecuteRoutine = async (routineId: string) => {
        try {
            await SellerbotService.executeRoutine(routineId);
            setLocalSuccess(getText('routineExecuted'));
            setTimeout(() => setLocalSuccess(null), 3000);
        } catch (error: any) {
            setLocalError(error.message || getText('errorExecutingRoutine'));
            setTimeout(() => setLocalError(null), 5000);
        }
    };

    const resetForm = () => {
        setRoutineFormData({
            name: '',
            taskType: 'follow_up',
            description: '',
            schedule: '0 */2 * * *',
            isActive: true,
            config: {
                priority: 'medium',
                maxExecutions: 10,
                timeout: 300,
                retryAttempts: 3
            }
        });
        setEditingRoutine(null);
    };

    // ==================== 🎨 FUNCIONES DE UTILIDAD ====================
    
    const getText = (key: string) => {
        const texts: Record<string, Record<string, string>> = {
            es: {
                title: 'Gestión de Rutinas',
                subtitle: 'Automatización inteligente de tareas del bot de ventas',
                createRoutine: 'Crear Rutina',
                editRoutine: 'Editar Rutina',
                loading: 'Cargando rutinas...',
                error: 'Error',
                noRoutines: 'No hay rutinas configuradas',
                active: 'Activa',
                inactive: 'Inactiva',
                running: 'Ejecutándose',
                idle: 'En espera',
                errorStatus: 'Error',
                edit: 'Editar',
                delete: 'Eliminar',
                execute: 'Ejecutar',
                toggle: 'Cambiar Estado',
                save: 'Guardar',
                cancel: 'Cancelar',
                confirmDelete: '¿Estás seguro de que quieres eliminar esta rutina?',
                name: 'Nombre de la Rutina',
                taskType: 'Tipo de Tarea',
                description: 'Descripción',
                schedule: 'Programación (Cron)',
                isActive: 'Rutina Activa',
                priority: 'Prioridad',
                maxExecutions: 'Máx. Ejecuciones',
                timeout: 'Timeout (seg)',
                retryAttempts: 'Reintentos',
                lastRun: 'Última Ejecución',
                nextRun: 'Próxima Ejecución',
                routineCreated: 'Rutina creada exitosamente',
                routineDeleted: 'Rutina eliminada exitosamente',
                routineToggled: 'Estado de rutina cambiado',
                routineExecuted: 'Rutina ejecutada manualmente',
                errorCreatingRoutine: 'Error al crear rutina',
                errorDeletingRoutine: 'Error al eliminar rutina',
                errorTogglingRoutine: 'Error al cambiar estado',
                errorExecutingRoutine: 'Error al ejecutar rutina',
                // Tipos de tareas
                follow_up: 'Seguimiento de Contactos',
                lead_generation: 'Generación de Leads',
                data_sync: 'Sincronización de Datos',
                report_generation: 'Generación de Reportes',
                auto_detect: 'Detección Automática',
                campaign_trigger: 'Activador de Campañas',
                // KPIs
                totalRoutines: 'Total Rutinas',
                activeRoutines: 'Rutinas Activas',
                executedToday: 'Ejecutadas Hoy',
                averageRuntime: 'Tiempo Promedio'
            },
            en: {
                title: 'Routine Management',
                subtitle: 'Intelligent automation of sales bot tasks',
                createRoutine: 'Create Routine',
                editRoutine: 'Edit Routine',
                loading: 'Loading routines...',
                error: 'Error',
                noRoutines: 'No routines configured',
                active: 'Active',
                inactive: 'Inactive',
                running: 'Running',
                idle: 'Idle',
                errorStatus: 'Error',
                edit: 'Edit',
                delete: 'Delete',
                execute: 'Execute',
                toggle: 'Toggle Status',
                save: 'Save',
                cancel: 'Cancel',
                confirmDelete: 'Are you sure you want to delete this routine?',
                name: 'Routine Name',
                taskType: 'Task Type',
                description: 'Description',
                schedule: 'Schedule (Cron)',
                isActive: 'Active Routine',
                priority: 'Priority',
                maxExecutions: 'Max Executions',
                timeout: 'Timeout (sec)',
                retryAttempts: 'Retry Attempts',
                lastRun: 'Last Run',
                nextRun: 'Next Run',
                routineCreated: 'Routine created successfully',
                routineDeleted: 'Routine deleted successfully',
                routineToggled: 'Routine status changed',
                routineExecuted: 'Routine executed manually',
                errorCreatingRoutine: 'Error creating routine',
                errorDeletingRoutine: 'Error deleting routine',
                errorTogglingRoutine: 'Error toggling routine',
                errorExecutingRoutine: 'Error executing routine',
                // Task types
                follow_up: 'Contact Follow-up',
                lead_generation: 'Lead Generation',
                data_sync: 'Data Synchronization',
                report_generation: 'Report Generation',
                auto_detect: 'Auto Detection',
                campaign_trigger: 'Campaign Trigger',
                // KPIs
                totalRoutines: 'Total Routines',
                activeRoutines: 'Active Routines',
                executedToday: 'Executed Today',
                averageRuntime: 'Avg Runtime'
            }
        };
        return texts[language]?.[key] || key;
    };

    const getStatusColor = (status: string) => {
        const colors = {
            running: 'success',
            idle: 'secondary',
            error: 'danger'
        };
        return colors[status as keyof typeof colors] || 'secondary';
    };

    const getTaskTypeIcon = (taskType: string) => {
        const icons = {
            follow_up: faRotateRight,
            lead_generation: faChartLine,
            data_sync: faSyncAlt,
            report_generation: faChartPie,
            auto_detect: faRobot,
            campaign_trigger: faBolt
        };
        return icons[taskType as keyof typeof icons] || faClock;
    };

    const formatCronExpression = (cron: string) => {
        // Simplificación básica de expresiones cron comunes
        const commonExpressions: Record<string, string> = {
            '0 */2 * * *': language === 'es' ? 'Cada 2 horas' : 'Every 2 hours',
            '0 9 * * *': language === 'es' ? 'Diario a las 9:00' : 'Daily at 9:00 AM',
            '0 9 * * 1-5': language === 'es' ? 'Lunes a Viernes 9:00' : 'Weekdays at 9:00 AM',
            '0 */6 * * *': language === 'es' ? 'Cada 6 horas' : 'Every 6 hours',
            '0 0 * * *': language === 'es' ? 'Diario a medianoche' : 'Daily at midnight'
        };
        
        return commonExpressions[cron] || cron;
    };

    // ==================== DATOS CALCULADOS ====================
    
    const routineStats = {
        total: routines.length,
        active: routines.filter(r => r.isActive).length,
        running: routines.filter(r => r.status === 'running').length,
        executedToday: routines.filter(r => {
            if (!r.lastRun) return false;
            const today = new Date().toDateString();
            const lastRun = new Date(r.lastRun).toDateString();
            return today === lastRun;
        }).length
    };

    // ==================== RENDER ====================

    if (loading) {
        return (
            <div className="routines-tab">
                <style>{customStyles}</style>
                <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p className="mt-3 text-muted">{getText('loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="routines-tab">
            <style>{customStyles}</style>
            
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-1 fw-bold">
                        <FontAwesomeIcon icon={faClock} className="me-3 text-primary" />
                        {getText('title')}
                    </h2>
                    <p className="text-muted mb-0">{getText('subtitle')}</p>
                </div>
                <Button 
                    variant="primary" 
                    onClick={() => setShowCreateModal(true)}
                    style={{ borderRadius: '12px' }}
                >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    {getText('createRoutine')}
                </Button>
            </div>

            {/* Alertas */}
            {(error || localError) && (
                <Alert variant="danger" className="mb-3" style={{ borderRadius: '12px' }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {localError || error}
                </Alert>
            )}
            {localSuccess && (
                <Alert variant="success" className="mb-3" style={{ borderRadius: '12px' }}>
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    {localSuccess}
                </Alert>
            )}

            {/* KPIs */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="kpi-card border-0 h-100" style={{ borderRadius: '16px' }}>
                        <Card.Body className="text-center">
                            <FontAwesomeIcon icon={faTimeline} size="2x" className="text-primary mb-2" />
                            <h3 className="mb-1 fw-bold">{routineStats.total}</h3>
                            <small className="text-muted">{getText('totalRoutines')}</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="kpi-card border-0 h-100" style={{ borderRadius: '16px' }}>
                        <Card.Body className="text-center">
                            <FontAwesomeIcon icon={faPlay} size="2x" className="text-success mb-2" />
                            <h3 className="mb-1 fw-bold">{routineStats.active}</h3>
                            <small className="text-muted">{getText('activeRoutines')}</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="kpi-card border-0 h-100" style={{ borderRadius: '16px' }}>
                        <Card.Body className="text-center">
                            <FontAwesomeIcon icon={faBolt} size="2x" className="text-warning mb-2" />
                            <h3 className="mb-1 fw-bold">{routineStats.running}</h3>
                            <small className="text-muted">{getText('running')}</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="kpi-card border-0 h-100" style={{ borderRadius: '16px' }}>
                        <Card.Body className="text-center">
                            <FontAwesomeIcon icon={faHistory} size="2x" className="text-info mb-2" />
                            <h3 className="mb-1 fw-bold">{routineStats.executedToday}</h3>
                            <small className="text-muted">{getText('executedToday')}</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Lista de Rutinas */}
            {routines.length === 0 ? (
                <Card className="text-center py-5" style={{ borderRadius: '16px' }}>
                    <Card.Body>
                        <FontAwesomeIcon icon={faClock} size="3x" className="text-muted mb-3" />
                        <h4 className="text-muted">{getText('noRoutines')}</h4>
                        <p className="text-muted mb-3">
                            {language === 'es' 
                                ? 'Crea tu primera rutina para automatizar tareas del bot' 
                                : 'Create your first routine to automate bot tasks'}
                        </p>
                        <Button 
                            variant="primary" 
                            onClick={() => setShowCreateModal(true)}
                            style={{ borderRadius: '12px' }}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            {getText('createRoutine')}
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <Row>
                    {routines.map((routine) => (
                        <Col md={6} lg={4} key={routine.id} className="mb-4">
                            <Card 
                                className={`routine-card border-0 h-100 status-${routine.status}`}
                                style={{ borderRadius: '16px' }}
                            >
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div className="d-flex align-items-center">
                                            <FontAwesomeIcon 
                                                icon={getTaskTypeIcon(routine.type)} 
                                                size="lg" 
                                                className="text-primary me-2" 
                                            />
                                            <div>
                                                <h6 className="mb-0 fw-bold">{routine.name}</h6>
                                                <small className="text-muted">{getText(routine.type)}</small>
                                            </div>
                                        </div>
                                        <Dropdown align="end">
                                            <Dropdown.Toggle 
                                                variant="light" 
                                                size="sm" 
                                                style={{ border: 'none', borderRadius: '8px' }}
                                            >
                                                <FontAwesomeIcon icon={faEllipsisVertical} />
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => handleExecuteRoutine(routine.id)}>
                                                    <FontAwesomeIcon icon={faPlay} className="me-2" />
                                                    {getText('execute')}
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => handleToggleRoutine(routine.id)}>
                                                    <FontAwesomeIcon 
                                                        icon={routine.isActive ? faPause : faPlay} 
                                                        className="me-2" 
                                                    />
                                                    {getText('toggle')}
                                                </Dropdown.Item>
                                                <Dropdown.Divider />
                                                <Dropdown.Item 
                                                    onClick={() => {
                                                        setSelectedRoutine(routine);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    className="text-danger"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                                    {getText('delete')}
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>

                                    <p className="text-muted small mb-3">{routine.description}</p>

                                    <div className="mb-3">
                                        <Badge className="schedule-badge me-2">
                                            <FontAwesomeIcon icon={faCalendarDays} className="me-1" />
                                            {formatCronExpression(routine.schedule)}
                                        </Badge>
                                        <Badge bg={getStatusColor(routine.status)}>
                                            {getText(routine.status)}
                                        </Badge>
                                    </div>

                                    <div className="mb-2">
                                        <small className="text-muted d-block">
                                            <strong>{getText('lastRun')}:</strong>{' '}
                                            {routine.lastRun 
                                                ? new Date(routine.lastRun).toLocaleString()
                                                : (language === 'es' ? 'Nunca' : 'Never')
                                            }
                                        </small>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            <strong>{getText('nextRun')}:</strong>{' '}
                                            {routine.nextRun 
                                                ? new Date(routine.nextRun).toLocaleString()
                                                : (language === 'es' ? 'No programada' : 'Not scheduled')
                                            }
                                        </small>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center">
                                        <Form.Check 
                                            type="switch"
                                            id={`routine-switch-${routine.id}`}
                                            label={routine.isActive ? getText('active') : getText('inactive')}
                                            checked={routine.isActive}
                                            onChange={() => handleToggleRoutine(routine.id)}
                                        />
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm"
                                            onClick={() => handleExecuteRoutine(routine.id)}
                                            style={{ borderRadius: '8px' }}
                                        >
                                            <FontAwesomeIcon icon={faPlay} />
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Modal Crear/Editar Rutina */}
            <Modal show={showCreateModal} onHide={() => {setShowCreateModal(false); resetForm();}} size="lg" centered>
                <Modal.Header closeButton style={{ borderRadius: '20px 20px 0 0', border: 'none' }}>
                    <Modal.Title className="fw-bold">
                        <FontAwesomeIcon icon={editingRoutine ? faEdit : faPlus} className="me-2 text-primary" />
                        {editingRoutine ? getText('editRoutine') : getText('createRoutine')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{getText('name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={routineFormData.name}
                                        onChange={(e) => setRoutineFormData({
                                            ...routineFormData,
                                            name: e.target.value
                                        })}
                                        placeholder={language === 'es' ? 'Ej: Seguimiento Diario' : 'Ex: Daily Follow-up'}
                                        style={{ borderRadius: '12px' }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{getText('taskType')}</Form.Label>
                                    <Form.Select
                                        value={routineFormData.taskType}
                                        onChange={(e) => setRoutineFormData({
                                            ...routineFormData,
                                            taskType: e.target.value as any
                                        })}
                                        style={{ borderRadius: '12px' }}
                                    >
                                        <option value="follow_up">{getText('follow_up')}</option>
                                        <option value="lead_generation">{getText('lead_generation')}</option>
                                        <option value="data_sync">{getText('data_sync')}</option>
                                        <option value="report_generation">{getText('report_generation')}</option>
                                        <option value="auto_detect">{getText('auto_detect')}</option>
                                        <option value="campaign_trigger">{getText('campaign_trigger')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>{getText('description')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={routineFormData.description}
                                onChange={(e) => setRoutineFormData({
                                    ...routineFormData,
                                    description: e.target.value
                                })}
                                placeholder={language === 'es' 
                                    ? 'Describe qué hace esta rutina...' 
                                    : 'Describe what this routine does...'}
                                style={{ borderRadius: '12px' }}
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{getText('schedule')}</Form.Label>
                                    <Form.Select
                                        value={routineFormData.schedule}
                                        onChange={(e) => setRoutineFormData({
                                            ...routineFormData,
                                            schedule: e.target.value
                                        })}
                                        style={{ borderRadius: '12px' }}
                                    >
                                        <option value="0 */2 * * *">{language === 'es' ? 'Cada 2 horas' : 'Every 2 hours'}</option>
                                        <option value="0 */6 * * *">{language === 'es' ? 'Cada 6 horas' : 'Every 6 hours'}</option>
                                        <option value="0 9 * * *">{language === 'es' ? 'Diario a las 9:00' : 'Daily at 9:00 AM'}</option>
                                        <option value="0 9 * * 1-5">{language === 'es' ? 'Lunes a Viernes 9:00' : 'Weekdays at 9:00 AM'}</option>
                                        <option value="0 0 * * *">{language === 'es' ? 'Diario a medianoche' : 'Daily at midnight'}</option>
                                        <option value="0 0 * * 0">{language === 'es' ? 'Semanal (Domingos)' : 'Weekly (Sundays)'}</option>
                                        <option value="0 0 1 * *">{language === 'es' ? 'Mensual (día 1)' : 'Monthly (1st day)'}</option>
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        {language === 'es' 
                                            ? 'O ingresa una expresión cron personalizada' 
                                            : 'Or enter a custom cron expression'}
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{getText('priority')}</Form.Label>
                                    <Form.Select
                                        value={routineFormData.config.priority}
                                        onChange={(e) => setRoutineFormData({
                                            ...routineFormData,
                                            config: {
                                                ...routineFormData.config,
                                                priority: e.target.value as any
                                            }
                                        })}
                                        style={{ borderRadius: '12px' }}
                                    >
                                        <option value="low">{language === 'es' ? 'Baja' : 'Low'}</option>
                                        <option value="medium">{language === 'es' ? 'Media' : 'Medium'}</option>
                                        <option value="high">{language === 'es' ? 'Alta' : 'High'}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Configuración Avanzada */}
                        <Card className="mb-3" style={{ borderRadius: '12px', backgroundColor: '#f8f9fa' }}>
                            <Card.Header style={{ borderRadius: '12px 12px 0 0', backgroundColor: '#e9ecef' }}>
                                <h6 className="mb-0">
                                    <FontAwesomeIcon icon={faCog} className="me-2" />
                                    {language === 'es' ? 'Configuración Avanzada' : 'Advanced Configuration'}
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{getText('maxExecutions')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="1"
                                                max="100"
                                                value={routineFormData.config.maxExecutions}
                                                onChange={(e) => setRoutineFormData({
                                                    ...routineFormData,
                                                    config: {
                                                        ...routineFormData.config,
                                                        maxExecutions: parseInt(e.target.value)
                                                    }
                                                })}
                                                style={{ borderRadius: '8px' }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{getText('timeout')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="30"
                                                max="3600"
                                                value={routineFormData.config.timeout}
                                                onChange={(e) => setRoutineFormData({
                                                    ...routineFormData,
                                                    config: {
                                                        ...routineFormData.config,
                                                        timeout: parseInt(e.target.value)
                                                    }
                                                })}
                                                style={{ borderRadius: '8px' }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{getText('retryAttempts')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                max="10"
                                                value={routineFormData.config.retryAttempts}
                                                onChange={(e) => setRoutineFormData({
                                                    ...routineFormData,
                                                    config: {
                                                        ...routineFormData.config,
                                                        retryAttempts: parseInt(e.target.value)
                                                    }
                                                })}
                                                style={{ borderRadius: '8px' }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Form.Group className="mb-3">
                            <Form.Check 
                                type="switch"
                                id="routine-active-switch"
                                label={getText('isActive')}
                                checked={routineFormData.isActive}
                                onChange={(e) => setRoutineFormData({
                                    ...routineFormData,
                                    isActive: e.target.checked
                                })}
                            />
                            <Form.Text className="text-muted">
                                {language === 'es' 
                                    ? 'La rutina se ejecutará automáticamente según la programación' 
                                    : 'The routine will run automatically according to the schedule'}
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer style={{ borderRadius: '0 0 20px 20px', border: 'none' }}>
                    <Button 
                        variant="secondary" 
                        onClick={() => {setShowCreateModal(false); resetForm();}}
                        style={{ borderRadius: '12px' }}
                    >
                        {getText('cancel')}
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleCreateRoutine}
                        disabled={!routineFormData.name || !routineFormData.description}
                        style={{ 
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #0d6efd, #6f42c1)',
                            border: 'none'
                        }}
                    >
                        <FontAwesomeIcon icon={editingRoutine ? faEdit : faPlus} className="me-2" />
                        {editingRoutine ? getText('save') : getText('createRoutine')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Confirmar Eliminación */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton style={{ borderRadius: '20px 20px 0 0', border: 'none' }}>
                    <Modal.Title className="fw-bold text-danger">
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        {getText('delete')} {getText('routine')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning mb-3" />
                        <h5>{getText('confirmDelete')}</h5>
                        {selectedRoutine && (
                            <p className="text-muted">
                                <strong>{selectedRoutine.name}</strong><br/>
                                {selectedRoutine.description}
                            </p>
                        )}
                        <Alert variant="warning" className="mt-3">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            {language === 'es' 
                                ? 'Esta acción no se puede deshacer. La rutina y su historial se eliminarán permanentemente.' 
                                : 'This action cannot be undone. The routine and its history will be permanently deleted.'}
                        </Alert>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ borderRadius: '0 0 20px 20px', border: 'none' }}>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowDeleteModal(false)}
                        style={{ borderRadius: '12px' }}
                    >
                        {getText('cancel')}
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDeleteRoutine}
                        style={{ borderRadius: '12px' }}
                    >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        {getText('delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default RoutinesTab;